import apiClient from './apiClient';
import {
  Syllabus,
  SyllabusYear,
  Stage,
  Strand,
  Substrand,
  ContentPoint,
  SyllabusContext,
  ApiResponse,
  PaginatedResponse,
  PaginationParams
} from '../types';

/**
 * Service for managing syllabus hierarchy navigation and content discovery
 * Provides methods to traverse the database-aligned content structure
 */

/**
 * Fetches all available years with grouped stages
 * @returns Promise resolving to array of years with their stages
 */
export const getYears = async (): Promise<SyllabusYear[]> => {
  try {
    const response = await apiClient.get<ApiResponse<SyllabusYear[]>>('/syllabus/years');
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch years');
    }
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching years:', error);
    throw error;
  }
};

/**
 * Fetches strands for a specific stage
 * @param stageId The stage ID
 * @returns Promise resolving to array of strands for the stage
 */
export const getStrandsByStage = async (stageId: string): Promise<Strand[]> => {
  try {
    const response = await apiClient.get<ApiResponse<Strand[]>>(`/syllabus/stages/${stageId}/strands`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch strands');
    }
    return response.data.data || [];
  } catch (error) {
    console.error(`Error fetching strands for stage ${stageId}:`, error);
    throw error;
  }
};

/**
 * Fetches substrands for a specific strand
 * @param strandId The strand ID
 * @param stageId Optional stage ID to filter substrands by stage
 * @returns Promise resolving to array of substrands for the strand
 */
export const getSubstrandsByStrand = async (strandId: string, stageId?: string): Promise<Substrand[]> => {
  try {
    const params = stageId ? { stageId } : {};
    const response = await apiClient.get<ApiResponse<Substrand[]>>(`/syllabus/strands/${strandId}/substrands`, { params });
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch substrands');
    }
    return response.data.data || [];
  } catch (error) {
    console.error(`Error fetching substrands for strand ${strandId}:`, error);
    throw error;
  }
};

/**
 * Fetches content points for a specific substrand
 * @param substrandId The substrand ID
 * @param pagination Optional pagination parameters
 * @returns Promise resolving to content points with pagination info
 */
export const getContentPointsBySubstrand = async (
  substrandId: string,
  pagination?: PaginationParams
): Promise<PaginatedResponse<ContentPoint>> => {
  try {
    const params = pagination ? {
      page: pagination.page,
      limit: pagination.limit,
      sortBy: pagination.sortBy,
      sortOrder: pagination.sortOrder
    } : {};

    const response = await apiClient.get<ApiResponse<PaginatedResponse<ContentPoint>>>(
      `/syllabus/substrands/${substrandId}/content-points`,
      { params }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch content points');
    }
    return response.data.data || { data: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 0 } };
  } catch (error) {
    console.error(`Error fetching content points for substrand ${substrandId}:`, error);
    throw error;
  }
};

/**
 * Fetches questions available for a specific content point
 * @param contentPointId The content point ID
 * @returns Promise resolving to array of questions
 */
export const getQuestionsByContentPoint = async (contentPointId: string) => {
  try {
    const response = await apiClient.get<ApiResponse<any[]>>(
      `/syllabus/content-points/${contentPointId}/questions`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch questions');
    }
    return response.data.data || [];
  } catch (error) {
    console.error(`Error fetching questions for content point ${contentPointId}:`, error);
    throw error;
  }
};

/**
 * Fetches full hierarchical context for a content point (breadcrumb information)
 * @param contentPointId The content point ID
 * @returns Promise resolving to complete syllabus context
 */
export const getContentPointContext = async (contentPointId: string): Promise<SyllabusContext> => {
  try {
    const response = await apiClient.get<ApiResponse<SyllabusContext>>(
      `/syllabus/content-points/${contentPointId}/context`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch context');
    }

    if (!response.data.data) {
      throw new Error('No context data returned');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error fetching context for content point ${contentPointId}:`, error);
    throw error;
  }
};

/**
 * Searches across the entire syllabus hierarchy
 * @param query Search query string
 * @param filters Optional search filters
 * @returns Promise resolving to search results
 */
export const searchSyllabus = async (
  query: string,
  filters?: {
    stageId?: string;
    strandId?: string;
    substrandId?: string;
    searchIn?: ('contentPoints' | 'outcomes')[];
  }
) => {
  try {
    const params = {
      q: query,
      ...filters,
      searchIn: filters?.searchIn?.join(',')
    };

    const response = await apiClient.get<ApiResponse<any>>('/syllabus/search', { params });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Search failed');
    }

    return response.data.data || [];
  } catch (error) {
    console.error(`Error searching syllabus for "${query}":`, error);
    throw error;
  }
};